import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Col, Spinner } from "reactstrap";
import { genericGet, genericPost, genericPut } from "services/services.general";
import { fetchAllRegions } from "services/services.congressman";
import {
  canEditRoles,
  BEFORE_TWENTIETH,
  TWENTIETH_CONGRESS,
  ALL_OPTION,
} from "utility/constants";
import { formatTimeToServer, parseTimeString, Toast } from "utility/helpers";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import { EDIT_ACTION, VIEW_ACTION } from "constants/actions";
import {
  HeaderText,
  StyledCancelButton,
  StyledSaveButton,
} from "assets/js/Styles";
import styled from "styled-components";
import { allRegion, customRegion, isNotAllOrCustom } from "../constants";
import EventFormSection from "./EventFormSection";

const BtnSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 3rem;
  padding-bottom: 4rem;
`;

const HeaderTextSection = styled.div`
  margin-top: 24px;
  display: grid;
  grid-auto-flow: column;
  justify-content: space-between;
  align-items: center;
`;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const AddEventForm = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { row, action } = location.state || null;

  const role = user?.type;

  const [form, setForm] = useState({
    eventName: "",
    eventStartDate: "",
    eventEndDate: "",
    eventStartTime: "",
    eventEndTime: "",
    eventVenue: "",
    eventDescription: "",
    excludedCongs: "",
    sendWithSMS: true,
    invitationFor: "",
    sendTo: "",
    rsvpStartDate: "",
    rsvpEndDate: "",
    message: "",
    attachedFileURL: "",
    officials: "",
  });

  const [recipients, setRecipients] = useState({
    congInvitees: [],
    excludedCongs: [],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [isCustomSelected, setIsCustomSelected] = useState(false);
  const [startEventDate, setEventStartDate] = useState(null);
  const [endEventDate, setEventEndDate] = useState(null);
  const [selectedStartTime, setSelectedStartTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [startRSVPDate, setRSVPStartDate] = useState(null);
  const [endRSVPDate, setRSVPEndDate] = useState(null);

  const [isLegacyData, setIsLegacyData] = useState(false);

  const navigate = useNavigate();

  const regions = regionOptions?.filter(isNotAllOrCustom) || [];

  const resetForm = () => {
    setRecipients({
      include: [],
      exclude: [],
    });

    setForm({
      eventName: "",
      eventStartDate: "",
      eventEndDate: "",
      eventStartTime: "",
      eventEndTime: "",
      eventVenue: "",
      eventDescription: "",
      excludedCongs: "",
      sendWithSMS: false,
      invitationFor: "",
      sendTo: "",
      rsvpStartDate: "",
      rsvpEndDate: "",
      message: "",
      attachedFileURL: "",
      officials: "",
    });

    setUploadedFiles([]);
    setSelectedRegion(null);
    setSelectedRecipients([]);
  };

  const sendData = (formData) => {
    genericPost({
      url: "/events",
      formData,
      callback: ({ isSuccess }) => {
        setIsLoading(false);

        if (isSuccess) {
          navigate("/events");

          Toast({
            type: 1,
            content: "Successfully created event.",
          });
        } else {
          Toast({
            type: 4,
            content: "An error occurred.",
          });
        }
      },
    });
  };

  const sendFinalDraft = (formData) => {
    genericPut({
      url: `/events/${row?.id}`,
      formData,
      callback: ({ isSuccess }) => {
        setIsLoading(false);

        if (isSuccess) {
          resetForm();
          navigate("/events");

          Toast({
            type: 1,
            content: "Successfully edited event.",
          });
        } else {
          Toast({
            type: 4,
            content: "An error occurred.",
          });
        }
      },
    });
  };

  const validate = () => {
    if (!form.eventName) {
      return "Event name cannot be empty";
    }

    if (!startEventDate || !endEventDate) {
      return "Event dates cannot be empty";
    }

    if (!selectedStartTime || !selectedEndTime) {
      return "Event times cannot be empty";
    }

    if (!form.eventVenue) {
      return "Event venue cannot be empty";
    }

    if (!selectedRegion) {
      return "Region cannot be empty";
    }

    if (form.invitationFor === "") {
      return "Invitee cannot be empty";
    }

    if (recipients.congInvitees.length === 0) {
      return "Recipients cannot be empty";
    }

    if (!startRSVPDate || !endRSVPDate) {
      return "RSVP dates cannot be empty";
    }

    if (!form.message && form.sendTo !== "") {
      return "Message cannot be empty when the checkbox is selected.";
    }

    return true;
  };

  const getRegionId = () => {
    if (selectedRegion.some((region) => region.value === 0)) return null;

    if (selectedRegion.some((region) => region.value === ALL_OPTION)) {
      return regions.map((op) => op.value).join(",");
    }

    return selectedRegion?.map((region) => region.value).join(",");
  };

  // eslint-disable-next-line consistent-return
  const handleSendOnClicked = () => {
    const isValid = validate();

    if (typeof isValid !== "boolean") {
      return Toast({
        type: 4,
        content: isValid,
      });
    }

    setIsLoading(true);

    const commonFields = {
      eventName: form.eventName,
      message: form.message,
      eventStartDate: startEventDate.toLocaleDateString("en-CA"),
      eventEndDate: endEventDate.toLocaleDateString("en-CA"),
      eventVenue: form.eventVenue,
      rsvpStartDate: startRSVPDate.toLocaleDateString("en-CA"),
      rsvpEndDate: endRSVPDate.toLocaleDateString("en-CA"),
    };

    const formData = {
      ...commonFields,
      eventStartTime: formatTimeToServer(selectedStartTime),
      eventEndTime: formatTimeToServer(selectedEndTime),
      eventDescription: form.eventDescription,
      congInvitees: recipients.congInvitees.join(";"),
      excludedCongs: recipients.excludedCongs.join(";"),
      sendWithSMS: form.sendWithSMS,
      sendTo: form.sendTo,
      invitationFor: form.invitationFor,
      attachedFileURL: uploadedFiles?.map((file) => file.location),
      regionId: getRegionId(),
    };

    if (row) {
      formData.id = row.id;
      sendFinalDraft(formData);
    } else {
      sendData(formData);
    }
  };

  const fetchRegions = () => {
    fetchAllRegions({
      params: {
        includeCentral: false,
      },
      callback: (data) => {
        if (data.length) {
          const options = data
            .filter(({ omegaId, id }) => omegaId !== null || id === 18)
            .map(({ acronym, id, omegaId }) => ({
              omegaId,
              label: acronym,
              value: id,
            }));

          options.unshift(
            {
              value: ALL_OPTION,
              label: "All",
            },
            {
              value: 0,
              label: "Custom",
            }
          );

          setRegionOptions(options);
        }
      },
    });
  };

  const getFilteredRegionOptions = () => {
    const all = selectedRegion?.length
      ? selectedRegion?.some((op) => op?.value === ALL_OPTION)
      : false;

    const custom = selectedRegion?.length
      ? selectedRegion?.some((op) => op?.value === 0)
      : false;

    if (custom) return [allRegion];

    return all ? [] : regionOptions;
  };

  const handleRegionOnChange = (option) => {
    if (option?.some((op) => op.value === 0)) {
      setSelectedRegion([customRegion]);
      setIsCustomSelected(true);

      return;
    }

    if (option.length > 1 && option?.some((op) => op.value === ALL_OPTION)) {
      setSelectedRegion([allRegion]);

      return;
    }

    if (option.length !== 0 && option.length === regions.length) {
      setSelectedRegion([allRegion]);

      return;
    }

    setIsCustomSelected(false);
    setSelectedRegion(option);
  };

  const setFromData = () => {
    if (new Date(row.createdAt) < BEFORE_TWENTIETH) setIsLegacyData(true);

    setForm({
      eventName: row.eventName,
      eventDescription: row.eventDescription,
      eventVenue: row.eventVenue,
      excludedCongs: row.excludedInvitees,
      message: row.message,
      invitationFor: row.invitationFor,
      sendTo: row.sendTo,
      sendWithSMS: row.sendWithSMS,
      sendWithEmail: row.sendWithEmail,
      eventStartDate: row.eventStartDate,
      eventEndDate: row.eventEndDate,
      eventStartTime: row.eventStartTime,
      eventEndTime: row.eventEndTime,
      rsvpStartDate: row.rsvpStartDate,
      rsvpEndDate: row.rsvpEndDate,
      attachedFileURL: row.attachedFileURL,
    });

    setEventStartDate(row.eventStartDate ? new Date(row.eventStartDate) : null);
    setEventEndDate(row.eventEndDate ? new Date(row.eventEndDate) : null);

    setSelectedStartTime(
      row.eventStartTime ? parseTimeString(row.eventStartTime) : null
    );

    setSelectedEndTime(
      row.eventEndTime ? parseTimeString(row.eventEndTime) : null
    );
    setRSVPStartDate(row.rsvpStartDate ? new Date(row.rsvpStartDate) : null);
    setRSVPEndDate(row.rsvpEndDate ? new Date(row.rsvpEndDate) : null);

    const files = [];

    if (row?.attachedFileURL?.length !== 0) {
      row?.attachedFileURL?.forEach((fileURL) => {
        const fileArray = fileURL.split("/");

        const file = {
          name: decodeURIComponent(fileArray[fileArray.length - 1]),
          location: fileURL,
          percent: 100,
        };

        files.push(file);
      });
    }

    setUploadedFiles(files);
  };

  const fetchAllRegionalCongs = () => {
    const regs = selectedRegion?.map((region) => region.value).join(",");

    const processData = (rows) => {
      const newData = rows.map(
        ({ id, fullName, District, contact, cosName, cosContact }) => ({
          name: fullName,
          id,
          area: District?.Region?.acronym || "",
          contact,
          cos: cosName,
          cosContact,
        })
      );

      setSelectedRecipients(newData);

      const included = row?.recipients?.split(";");

      const includedIds = !included
        ? rows.map(({ id }) => id)
        : rows
            .filter(({ id }) => included.includes(id.toString()))
            .map(({ id }) => id);

      const excludedIds =
        form.excludedCongs !== ""
          ? form.excludedCongs.split(";").map(Number)
          : [];

      const finalIncludedIds = includedIds.filter(
        (id) => !excludedIds.includes(id)
      );

      setRecipients({
        ...recipients,
        congInvitees: finalIncludedIds,
      });
    };

    const params = {
      region: regs,
      pagination: false,
      ...(isLegacyData ? {} : { congress: TWENTIETH_CONGRESS }),
    };

    genericGet({
      url: "congressman/lookup/regions",
      params,
      callback: (data) => {
        if (data?.rows?.length !== 0) {
          processData(data.rows);
        } else {
          setSelectedRecipients([]);

          setRecipients({
            ...recipients,
            congInvitees: [],
          });
        }
      },
    });
  };

  const setSelectedCustomRecipients = () => {
    setIsLoading(true);

    let included = row?.eventParticipantIds?.split(",") || [];
    included = [...new Set(included.map((id) => Number(id)))];
    const includedSet = new Set(included);

    const mapRecipients = (data = []) =>
      data
        .filter((item) => includedSet.has(item.id))
        .map((item) => ({
          name: item.name,
          id: item.id,
          designation: "Congressman",
          contact: item.contact,
          agency: "",
          area: item?.region?.acronym ?? "",
          cos: item.cos?.name,
          cosContact: item.cos?.contact,
        }));

    genericGet({
      url: "/congressman",
      params: !isLegacyData ? { congress: TWENTIETH_CONGRESS } : undefined,
      callback: (data = []) => {
        const selecRecipients = mapRecipients(data);
        setSelectedRecipients(selecRecipients);

        setRecipients({
          ...recipients,
          congInvitees: included,
        });

        setIsLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  useEffect(() => {
    if (canEditRoles.includes(role)) {
      if (row && regionOptions.length) {
        if (row.regionIds) {
          const regs = row.regionIds?.split(",");

          const region = regionOptions.filter((reg) =>
            regs.includes(reg.value.toString())
          );

          if (regions.length === regs.length) setSelectedRegion([allRegion]);
          else setSelectedRegion(region);
        } else {
          setSelectedRegion([customRegion]);
          setSelectedCustomRecipients();
        }
      }
    }
  }, [row, regionOptions]);

  useEffect(() => {
    if (row) setFromData();
  }, [row, action]);

  useEffect(() => {
    if (selectedRegion) {
      if (
        selectedRegion.length &&
        selectedRegion.some((op) => op.value !== 0)
      ) {
        fetchAllRegionalCongs();
      } else {
        setSelectedRecipients([]);

        setRecipients({
          ...recipients,
          congInvitees: [],
        });
      }
    }
  }, [selectedRegion]);

  const getHeaderText = () => {
    if (action === EDIT_ACTION) return "Edit an Event";
    if (action === VIEW_ACTION) return "Event Details";

    return "Add an Event";
  };

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <Wrapper>
      <Col className="container">
        <HeaderTextSection>
          <HeaderText>{getHeaderText()}</HeaderText>
        </HeaderTextSection>

        <EventFormSection
          form={form}
          setForm={setForm}
          action={action}
          startEventDate={startEventDate}
          setEventStartDate={setEventStartDate}
          endEventDate={endEventDate}
          setEventEndDate={setEventEndDate}
          selectedStartTime={selectedStartTime}
          setSelectedStartTime={setSelectedStartTime}
          selectedEndTime={selectedEndTime}
          setSelectedEndTime={setSelectedEndTime}
          role={role}
          handleRegionOnChange={handleRegionOnChange}
          selectedRegion={selectedRegion}
          getFilteredRegionOptions={getFilteredRegionOptions}
          selectedRecipients={selectedRecipients}
          setRecipients={setRecipients}
          recipients={recipients}
          isCustomSelected={isCustomSelected}
          isLegacyData={isLegacyData}
          startRSVPDate={startRSVPDate}
          setRSVPStartDate={setRSVPStartDate}
          endRSVPDate={endRSVPDate}
          setRSVPEndDate={setRSVPEndDate}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
        />

        <BtnSection>
          <StyledCancelButton
            outline
            disabled={isLoading}
            onClick={() => navigate(-1)}
          >
            {"Cancel"}
          </StyledCancelButton>

          <StyledSaveButton
            color="primary"
            onClick={() => handleSendOnClicked()}
            disabled={isLoading || action === VIEW_ACTION}
          >
            {isLoading ? (
              <Spinner
                animation="border"
                color="light"
                size="sm"
              />
            ) : (
              "Save"
            )}
          </StyledSaveButton>
        </BtnSection>
      </Col>
    </Wrapper>
  );
};

export default AddEventForm;
